import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { 
  HiCheckCircle, 
  HiExclamationTriangle, 
  HiInformationCircle, 
  HiXCircle,
  HiXMark 
} from 'react-icons/hi2';
import { usePerformance } from '../../contexts/PerformanceContext';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  dismissible?: boolean;
}

interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
  index: number;
}

const getToastIcon = (type: ToastType) => {
  switch (type) {
    case 'success':
      return <HiCheckCircle className="w-5 h-5 text-green-500" />;
    case 'error':
      return <HiXCircle className="w-5 h-5 text-red-500" />;
    case 'warning':
      return <HiExclamationTriangle className="w-5 h-5 text-yellow-500" />;
    case 'info':
      return <HiInformationCircle className="w-5 h-5 text-blue-500" />;
  }
};

const getToastColors = (type: ToastType) => {
  switch (type) {
    case 'success':
      return {
        border: 'border-green-200/50 dark:border-green-700/50',
        iconBg: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20',
        progressBar: 'bg-green-500',
        tintedGlass: {
          baseLayer: 'rgba(34, 197, 94, 0.15)', // green-500 with moderate opacity (+20%)
          mainLayer: `linear-gradient(135deg, 
            rgba(34, 197, 94, 0.25) 0%, 
            rgba(34, 197, 94, 0.18) 25%,
            rgba(255, 255, 255, 0.30) 50%, 
            rgba(34, 197, 94, 0.18) 75%,
            rgba(34, 197, 94, 0.20) 100%)`,
          border: '1px solid rgba(34, 197, 94, 0.30)'
        }
      };
    case 'error':
      return {
        border: 'border-red-200/50 dark:border-red-700/50',
        iconBg: 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20',
        progressBar: 'bg-red-500',
        tintedGlass: {
          baseLayer: 'rgba(239, 68, 68, 0.15)', // red-500 with moderate opacity (+20%)
          mainLayer: `linear-gradient(135deg, 
            rgba(239, 68, 68, 0.25) 0%, 
            rgba(239, 68, 68, 0.18) 25%,
            rgba(255, 255, 255, 0.30) 50%, 
            rgba(239, 68, 68, 0.18) 75%,
            rgba(239, 68, 68, 0.20) 100%)`,
          border: '1px solid rgba(239, 68, 68, 0.30)'
        }
      };
    case 'warning':
      return {
        border: 'border-yellow-200/50 dark:border-yellow-700/50',
        iconBg: 'bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20',
        progressBar: 'bg-yellow-500',
        tintedGlass: {
          baseLayer: 'rgba(234, 179, 8, 0.15)', // yellow-500 with moderate opacity (+20%)
          mainLayer: `linear-gradient(135deg, 
            rgba(234, 179, 8, 0.25) 0%, 
            rgba(234, 179, 8, 0.18) 25%,
            rgba(255, 255, 255, 0.30) 50%, 
            rgba(234, 179, 8, 0.18) 75%,
            rgba(234, 179, 8, 0.20) 100%)`,
          border: '1px solid rgba(234, 179, 8, 0.30)'
        }
      };
    case 'info':
      return {
        border: 'border-blue-200/50 dark:border-blue-700/50',
        iconBg: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20',
        progressBar: 'bg-blue-500',
        tintedGlass: {
          baseLayer: 'rgba(59, 130, 246, 0.15)', // blue-500 with moderate opacity (+20%)
          mainLayer: `linear-gradient(135deg, 
            rgba(59, 130, 246, 0.25) 0%, 
            rgba(59, 130, 246, 0.18) 25%,
            rgba(255, 255, 255, 0.30) 50%, 
            rgba(59, 130, 246, 0.18) 75%,
            rgba(59, 130, 246, 0.20) 100%)`,
          border: '1px solid rgba(59, 130, 246, 0.30)'
        }
      };
  }
};

const Toast: React.FC<ToastProps> = ({ toast, onDismiss, index }) => {
  const [progress, setProgress] = useState(100);
  const [isHovered, setIsHovered] = useState(false);
  const { performanceMode } = usePerformance();
  const colors = getToastColors(toast.type);
  const duration = toast.duration || 4000;
  const isPerformanceMode = performanceMode === 'performance';

  useEffect(() => {
    if (isHovered) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (duration / 100));
        if (newProgress <= 0) {
          onDismiss(toast.id);
          return 0;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [toast.id, onDismiss, duration, isHovered]);

  const handleDismiss = useCallback(() => {
    onDismiss(toast.id);
  }, [toast.id, onDismiss]);

  return (
    <motion.div
      key={toast.id}
      initial={isPerformanceMode ? false : { 
        opacity: 0, 
        x: 400, 
        scale: 0.9,
      }}
      animate={{ 
        opacity: 1, 
        x: 0, 
        scale: 1,
        y: index * 85, // Stack toasts with spacing
      }}
      exit={isPerformanceMode ? { opacity: 0 } : { 
        opacity: 0, 
        x: 400, 
        scale: 0.8,
        transition: { duration: 0.2 }
      }}
      transition={isPerformanceMode ? { duration: 0.15 } : {
        type: "spring",
        stiffness: 500,
        damping: 30,
        mass: 0.8
      }}
      className="relative w-80 mb-3 pointer-events-auto"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        willChange: 'transform, opacity',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
      whileHover={isPerformanceMode ? {} : {
        scale: 1.01,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={isPerformanceMode ? {} : { 
        scale: 0.99,
        transition: { duration: 0.1 }
      }}
    >
      {/* Ambient Glow Background - Only in beauty mode */}
      {!isPerformanceMode && (
        <div 
          className="absolute inset-0 rounded-[20px] opacity-40"
          style={{
            background: `radial-gradient(ellipse at center, 
              ${colors.tintedGlass.baseLayer} 0%,
              transparent 60%)`,
            transform: 'scale(1.1) translateY(2px)',
            filter: 'blur(8px)',
            zIndex: -1
          }}
        />
      )}
      
      {/* Main Toast Container */}
      <div
        className="relative overflow-hidden"
        style={{
          borderRadius: '20px',
          boxShadow: isPerformanceMode 
            ? `0 4px 8px -2px rgba(0, 0, 0, 0.20),
               0 0 0 1px ${colors.tintedGlass.baseLayer}`
            : `0 20px 40px -8px rgba(0, 0, 0, 0.20),
               0 12px 24px -6px rgba(0, 0, 0, 0.15),
               0 6px 12px -3px rgba(0, 0, 0, 0.10),
               0 0 0 1px rgba(255, 255, 255, 0.15),
               0 0 0 2px ${colors.tintedGlass.baseLayer},
               inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
          transform: 'translateZ(0)',
        }}
      >
        {/* Tinted Glass Base Layer */}
        <div
          className="absolute inset-0 z-10"
          style={{
            borderRadius: 'inherit',
            backdropFilter: isPerformanceMode ? "blur(8px)" : "blur(20px)",
            background: `linear-gradient(135deg, 
              ${colors.tintedGlass.baseLayer} 0%,
              rgba(255, 255, 255, 0.25) 100%)`,
            transform: 'translateZ(0)',
          }}
        />
        
        {/* Tinted Main Glass Layer */}
        <div 
          className="absolute inset-0 z-20"
          style={{
            borderRadius: 'inherit',
            background: isPerformanceMode 
              ? `linear-gradient(135deg, ${colors.tintedGlass.baseLayer} 0%, rgba(255, 255, 255, 0.10) 100%)`
              : colors.tintedGlass.mainLayer,
            backdropFilter: isPerformanceMode ? "blur(4px)" : "blur(6px)",
            border: colors.tintedGlass.border,
          }}
        />

        {/* Inner Glass Reflection - Only in beauty mode */}
        {!isPerformanceMode && (
          <div
            className="absolute inset-0 z-30"
            style={{
              borderRadius: 'inherit',
              background: `linear-gradient(145deg, 
                rgba(255,255,255,0.5) 0%, 
                transparent 25%,
                transparent 75%,
                rgba(255,255,255,0.3) 100%)`,
              boxShadow: `
                inset 1px 1px 2px rgba(255,255,255,0.5), 
                inset -1px -1px 2px rgba(255,255,255,0.2)`
            }}
          />
        )}
        
        {/* Surface Highlights with Tint - Only in beauty mode */}
        {!isPerformanceMode && (
          <div
            className="absolute inset-0 z-40 pointer-events-none"
            style={{
              borderRadius: 'inherit',
              background: `radial-gradient(circle at 30% 20%, 
                rgba(255,255,255,0.6) 0%, 
                ${colors.tintedGlass.baseLayer} 30%,
                transparent 50%)`,
              opacity: 0.4
            }}
          />
        )}

        {/* Content Container */}
        <div 
          className={`relative z-50 p-4 ${isPerformanceMode ? '' : 'backdrop-blur-[2px]'}`}
          style={{ 
            borderRadius: 'inherit',
            background: isPerformanceMode
              ? `linear-gradient(135deg, 
                  ${colors.tintedGlass.baseLayer} 0%,
                  rgba(255, 255, 255, 0.15) 100%)`
              : `linear-gradient(135deg, 
                  ${colors.tintedGlass.baseLayer} 0%,
                  rgba(255, 255, 255, 0.18) 50%,
                  rgba(0, 0, 0, 0.08) 100%)`
          }}
        >
          <div className="flex items-start space-x-3">
            {/* Icon */}
            <div 
              className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 ${colors.iconBg} ${colors.border} border`}
              style={{
                boxShadow: isPerformanceMode
                  ? `0 2px 4px -1px rgba(0, 0, 0, 0.10)`
                  : `0 2px 4px -1px rgba(0, 0, 0, 0.10),
                     0 0 0 1px rgba(255, 255, 255, 0.1),
                     inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
                transform: 'translateZ(0)'
              }}
            >
              {getToastIcon(toast.type)}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <p 
                className="text-sm font-medium text-gray-900 dark:text-white leading-5 break-words"
                style={!isPerformanceMode ? {
                  textShadow: `0 1px 2px rgba(0, 0, 0, 0.1),
                               0 0 4px rgba(255, 255, 255, 0.2)`
                } : {}}
              >
                {toast.message}
              </p>
            </div>
            
            {/* Close Button */}
            {toast.dismissible !== false && (
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white/10 transition-all duration-200"
                style={!isPerformanceMode ? {
                  boxShadow: `0 1px 2px -1px rgba(0, 0, 0, 0.08),
                             inset 0 1px 0 rgba(255, 255, 255, 0.1)`,
                  transform: 'translateZ(0)'
                } : { transform: 'translateZ(0)' }}
                title="Fechar notificação"
              >
                <HiXMark className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Progress Bar */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-1" 
            style={{ 
              borderRadius: '0 0 20px 20px',
              background: isPerformanceMode
                ? `rgba(0, 0, 0, 0.1)`
                : `linear-gradient(90deg, 
                    ${colors.tintedGlass.baseLayer} 0%,
                    rgba(0, 0, 0, 0.1) 50%,
                    ${colors.tintedGlass.baseLayer} 100%)`
            }}
          >
            <motion.div
              className={`h-full ${colors.progressBar}`}
              style={{ 
                borderRadius: '0 0 20px 20px',
                width: `${progress}%`,
                transformOrigin: 'left center'
              }}
              animate={{ 
                width: `${progress}%`,
                opacity: isHovered ? 0.5 : 1
              }}
              transition={{ duration: isPerformanceMode ? 0 : 0.1 }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface GlassToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

export const GlassToastContainer: React.FC<GlassToastContainerProps> = ({ 
  toasts, 
  onDismiss, 
  position = 'top-right' 
}) => {
  const getPositionClasses = () => {
    switch (position) {
      case 'top-right':
        return 'top-6 right-6';
      case 'top-left':
        return 'top-6 left-6';
      case 'bottom-right':
        return 'bottom-6 right-6';
      case 'bottom-left':
        return 'bottom-6 left-6';
      case 'top-center':
        return 'top-6 left-1/2 transform -translate-x-1/2';
      case 'bottom-center':
        return 'bottom-6 left-1/2 transform -translate-x-1/2';
      default:
        return 'top-6 right-6';
    }
  };

  if (toasts.length === 0) return null;

  return createPortal(
    <div className={`fixed z-[10000] pointer-events-none ${getPositionClasses()}`}>
      <AnimatePresence mode="popLayout">
        {toasts.map((toast, index) => (
          <Toast
            key={toast.id}
            toast={toast}
            onDismiss={onDismiss}
            index={index}
          />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};
